import { defineStore } from 'pinia';
import { BaseModel } from '~/api/models';

export interface List<T extends BaseModel> {
    [key: string]: any;
    page: number;
    perPage: number;
    search: string | undefined;
    sort: { key: string; direction: 'asc' | 'desc' } | undefined;
    results: T[];
    meta: Record<string, any>;
    pagination: PaginationResponse;
    checked: T[];
    refresh: () => Promise<void>;
}

export interface ListsStore {
    data: Record<string, List<any>>;
}

interface SetListAttribute {
    serviceId: string;
    key: any;
    value: any;
}

export const useListsStore = defineStore('lists', {
    state: (): ListsStore => ({
        data: {},
    }),

    getters: {
        hasLists: (state) => Object.keys(state.data).length > 0,

        getList: (state) => (serviceId: string) => {
            return state.data[serviceId];
        },

        hasList: (state) => (serviceId: string) => {
            return serviceId in state.data;
        },

        getListResults:
            (state) =>
            <T extends BaseModel>(serviceId: string): T[] => {
                return state.data[serviceId]?.results || [];
            },

        getListPagination: (state) => (serviceId: string) => {
            return state.data[serviceId]?.pagination;
        },

        getListMeta: (state) => (serviceId: string) => {
            return state.data[serviceId]?.meta || {};
        },

        getCheckedItems:
            (state) =>
            <T extends BaseModel>(serviceId: string): T[] => {
                return state.data[serviceId]?.checked || [];
            },

        hasCheckedItems: (state) => (serviceId: string) => {
            return (state.data[serviceId]?.checked || []).length > 0;
        },

        getListPage: (state) => (serviceId: string) => {
            return state.data[serviceId]?.page || 1;
        },

        getListSearch: (state) => (serviceId: string) => {
            return state.data[serviceId]?.search;
        },

        getListSort: (state) => (serviceId: string) => {
            return state.data[serviceId]?.sort;
        },

        isLoading: (state) => (serviceId: string) => {
            // Check if the list has results but pagination shows more items than results
            // This indicates a loading state (skeleton items)
            const list = state.data[serviceId];
            if (!list) return false;

            return list.results.length === 0 && list.pagination.total > 0;
        },

        getAllServiceIds: (state) => Object.keys(state.data),

        getListCount: (state) => (serviceId: string) => {
            return state.data[serviceId]?.pagination?.count || 0;
        },

        getListTotal: (state) => (serviceId: string) => {
            return state.data[serviceId]?.pagination?.total || 0;
        },

        hasMorePages: (state) => (serviceId: string) => {
            const list = state.data[serviceId];
            if (!list) return false;
            return list.pagination.currentPage < list.pagination.totalPages;
        },

        hasPreviousPages: (state) => (serviceId: string) => {
            const list = state.data[serviceId];
            if (!list) return false;
            return list.pagination.currentPage > 1;
        },
    },

    actions: {
        add(serviceId: string) {
            if (serviceId in this.data) {
                // This in theory can happen when some page explicitly doesn't use namespaces for service, and they end up conflicting
                throw new Error(`Service ID ${serviceId} already has a list???`);
            }

            this.data[serviceId] = {
                page: 1,
                perPage: 25,
                search: undefined,
                sort: undefined,
                results: [],
                meta: {},
                pagination: {
                    count: 0,
                    currentPage: 1,
                    total: 0,
                    totalPages: 1,
                    perPage: 25,
                },
                checked: [],
                refresh: async () => {
                    throw new Error('Refresh handler for list failed to register???');
                },
            };
        },

        set(data: SetListAttribute) {
            const list = this.data[data.serviceId];
            if (!list) return;

            list[data.key] = data.value;
        },

        async refresh(serviceId: string) {
            const list = this.data[serviceId];
            if (list?.refresh) {
                await list.refresh();
            }
        },

        delete(serviceId: string) {
            delete this.data[serviceId];
        },

        // Enhanced actions for better usability
        setResults<T extends BaseModel>(serviceId: string, results: T[]) {
            this.set({ serviceId, key: 'results', value: results });
        },

        setPagination(serviceId: string, pagination: PaginationResponse) {
            this.set({ serviceId, key: 'pagination', value: pagination });
        },

        setMeta(serviceId: string, meta: Record<string, any>) {
            this.set({ serviceId, key: 'meta', value: meta });
        },

        setPage(serviceId: string, page: number) {
            this.set({ serviceId, key: 'page', value: page });
        },

        setPerPage(serviceId: string, perPage: number) {
            this.set({ serviceId, key: 'perPage', value: perPage });
        },

        setSearch(serviceId: string, search: string | undefined) {
            this.set({ serviceId, key: 'search', value: search });
        },

        setSort(serviceId: string, sort: { key: string; direction: 'asc' | 'desc' } | undefined) {
            this.set({ serviceId, key: 'sort', value: sort });
        },

        setChecked<T extends BaseModel>(serviceId: string, checked: T[]) {
            this.set({ serviceId, key: 'checked', value: checked });
        },

        addChecked<T extends BaseModel>(serviceId: string, item: T) {
            const list = this.data[serviceId];
            if (list && !list.checked.includes(item)) {
                list.checked.push(item);
            }
        },

        removeChecked<T extends BaseModel>(serviceId: string, item: T) {
            const list = this.data[serviceId];
            if (list) {
                const index = list.checked.indexOf(item);
                if (index !== -1) {
                    list.checked.splice(index, 1);
                }
            }
        },

        clearChecked(serviceId: string) {
            this.set({ serviceId, key: 'checked', value: [] });
        },

        toggleChecked<T extends BaseModel>(serviceId: string, item: T) {
            const list = this.data[serviceId];
            if (list) {
                const index = list.checked.indexOf(item);
                if (index !== -1) {
                    list.checked.splice(index, 1);
                } else {
                    list.checked.push(item);
                }
            }
        },

        setRefreshHandler(serviceId: string, refreshHandler: () => Promise<void>) {
            this.set({ serviceId, key: 'refresh', value: refreshHandler });
        },

        // Batch operations
        updateList<T extends BaseModel>(
            serviceId: string,
            updates: {
                results?: T[];
                pagination?: PaginationResponse;
                meta?: Record<string, any>;
                page?: number;
                perPage?: number;
                search?: string | undefined;
                sort?: { key: string; direction: 'asc' | 'desc' } | undefined;
            },
        ) {
            const list = this.data[serviceId];
            if (!list) return;

            if (updates.results !== undefined) list.results = updates.results;
            if (updates.pagination !== undefined) list.pagination = updates.pagination;
            if (updates.meta !== undefined) list.meta = updates.meta;
            if (updates.page !== undefined) list.page = updates.page;
            if (updates.perPage !== undefined) list.perPage = updates.perPage;
            if (updates.search !== undefined) list.search = updates.search;
            if (updates.sort !== undefined) list.sort = updates.sort;
        },

        // Clear all lists
        clearAll() {
            this.data = {};
        },

        // Reset a specific list to initial state
        reset(serviceId: string) {
            const list = this.data[serviceId];
            if (list) {
                const refreshHandler = list.refresh;
                this.data[serviceId] = {
                    page: 1,
                    perPage: list.perPage,
                    search: undefined,
                    sort: undefined,
                    results: [],
                    meta: {},
                    pagination: {
                        count: 0,
                        currentPage: 1,
                        total: 0,
                        totalPages: 1,
                        perPage: list.perPage,
                    },
                    checked: [],
                    refresh: refreshHandler,
                };
            }
        },
    },
});
